import {
  type CaptionFrame,
  type CaptionPage,
  type CaptionsElement,
  type CaptionWordState,
  captionFrameAt,
} from "@cva/shared";
import type { CSSProperties, ReactNode } from "react";
import { boxStyle, fontFamilyCss } from "./styles";

/**
 * Captions element: the caption visible at time `t`, word by word, with the
 * style and animation of the element. A pure function of (element, pages, t).
 */

const JUSTIFY = { top: "flex-start", middle: "center", bottom: "flex-end" } as const;
const ALIGN = { left: "flex-start", center: "center", right: "flex-end" } as const;

function textStyle(element: CaptionsElement): CSSProperties {
  const style = element.style;
  return {
    fontFamily: fontFamilyCss(style.fontFamily),
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.italic ? "italic" : "normal",
    fontSynthesis: "none",
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing ? `${style.letterSpacing}em` : undefined,
    // A little extra room: the active word grows (pop, emphasis) without touching its neighbours.
    wordSpacing: "0.12em",
    textTransform: style.uppercase ? "uppercase" : undefined,
    color: style.color,
    textAlign: style.align,
    textRendering: "geometricPrecision",
    WebkitTextStroke:
      style.stroke && style.stroke.width > 0
        ? `${style.stroke.width * 2}px ${style.stroke.color}`
        : undefined,
    paintOrder: style.stroke ? "stroke fill" : undefined,
    textShadow: style.shadow
      ? `${style.shadow.x}px ${style.shadow.y}px ${style.shadow.blur}px ${style.shadow.color}`
      : undefined,
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    margin: 0,
  };
}

function Word({ element, state }: { element: CaptionsElement; state: CaptionWordState }) {
  const style = element.style;
  const boxed = style.background === "word";
  const highlightBox = state.active && style.highlight === "box";
  // Keywords keep their accent for the whole caption; the spoken-word highlight wins.
  const emphasis = Boolean(state.word.emphasis);
  const scale = state.scale * (emphasis ? (style.emphasisScale ?? 1) : 1);
  const transforms: string[] = [];
  if (state.y) transforms.push(`translateY(${state.y}px)`);
  if (scale !== 1) transforms.push(`scale(${Math.max(0, scale)})`);
  const activeColor = state.active
    ? style.highlight === "color"
      ? style.highlightColor
      : highlightBox
        ? style.highlightTextColor
        : undefined
    : undefined;
  const color =
    activeColor ?? (emphasis ? (style.emphasisColor ?? style.highlightColor) : undefined);
  let content: ReactNode = state.word.text;
  if (state.reveal !== null) {
    const chars = [...state.word.text];
    const shown = Math.round(state.reveal * chars.length);
    content = (
      <>
        {chars.slice(0, shown).join("")}
        <span style={{ visibility: "hidden" }}>{chars.slice(shown).join("")}</span>
      </>
    );
  }
  const wordStyle: CSSProperties = {
    display: "inline-block",
    position: "relative",
    visibility: state.visible ? undefined : "hidden",
    opacity: state.opacity === 1 ? undefined : state.opacity,
    transform: transforms.length > 0 ? transforms.join(" ") : undefined,
    transformOrigin: "50% 60%",
    color,
    fontWeight: emphasis && style.emphasisBold ? 900 : undefined,
    // Room for a larger keyword so it does not touch its neighbours.
    margin: emphasis && scale > 1 ? `0 ${((scale - 1) * 0.5).toFixed(3)}em` : undefined,
    textDecorationLine: state.active && style.highlight === "underline" ? "underline" : undefined,
    textDecorationColor: style.highlightColor,
    textDecorationThickness: "0.1em",
    textUnderlineOffset: "0.14em",
    ...(boxed || highlightBox
      ? {
          background: highlightBox ? style.highlightColor : style.backgroundColor,
          borderRadius: style.backgroundRadius,
          padding: "0.02em 0.2em",
          margin: "0 -0.08em",
        }
      : {}),
  };
  return <span style={wordStyle}>{content}</span>;
}

function Line({
  element,
  words,
  onlyVisible,
}: {
  element: CaptionsElement;
  words: CaptionWordState[];
  onlyVisible: boolean;
}) {
  const style = element.style;
  const shown = onlyVisible ? words.filter((state) => state.visible) : words;
  if (shown.length === 0) return null;
  const children: ReactNode[] = [];
  shown.forEach((state, index) => {
    if (index > 0) children.push(" ");
    children.push(<Word key={state.word.key} element={element} state={state} />);
  });
  if (style.background === "line") {
    return (
      <div>
        <span
          style={{
            background: style.backgroundColor,
            borderRadius: style.backgroundRadius,
            padding: "0.06em 0.32em",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
          }}
        >
          {children}
        </span>
      </div>
    );
  }
  return <div>{children}</div>;
}

export function CaptionFrameView({
  element,
  frame,
}: {
  element: CaptionsElement;
  frame: CaptionFrame;
}) {
  const style = element.style;
  const onlyVisible = element.captionAnimation.preset === "word-by-word";
  return (
    <div
      style={{
        ...textStyle(element),
        opacity: frame.opacity === 1 ? undefined : frame.opacity,
        transform: frame.y ? `translateY(${frame.y}px)` : undefined,
        maxWidth: "100%",
        ...(style.background === "page"
          ? {
              background: style.backgroundColor,
              borderRadius: style.backgroundRadius,
              padding: "0.25em 0.45em",
            }
          : {}),
      }}
    >
      {frame.lines.map((line, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lines of a caption are positional
        <Line key={index} element={element} words={line} onlyVisible={onlyVisible} />
      ))}
    </div>
  );
}

/** Frame with sample words (editor: shows the style while no caption is spoken). */
function placeholderFrame(text: string): CaptionFrame {
  const words = text.split(/\s+/).filter(Boolean);
  const page: CaptionPage = {
    index: -1,
    start: 0,
    end: 1,
    lines: [
      words.map((word, index) => ({
        key: `placeholder-${index}`,
        text: word,
        start: index,
        end: index + 1,
        breakBefore: false,
      })),
    ],
  };
  return {
    page,
    opacity: 1,
    y: 0,
    lines: page.lines.map((line) =>
      line.map((word, index) => ({
        word,
        visible: true,
        active: index === 1,
        spoken: true,
        opacity: 1,
        scale: 1,
        y: 0,
        reveal: null,
      })),
    ),
  };
}

export function renderCaptions(
  element: CaptionsElement,
  options: {
    time: number | undefined;
    pages: readonly CaptionPage[] | undefined;
    placeholder: string | undefined;
  },
): ReactNode {
  let frame =
    options.time !== undefined && options.pages
      ? captionFrameAt(element, options.pages, options.time)
      : null;
  let placeholder = false;
  if (!frame && options.placeholder) {
    frame = placeholderFrame(options.placeholder);
    placeholder = true;
  }
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      data-captions=""
      style={{
        ...boxStyle(element),
        display: "flex",
        flexDirection: "column",
        justifyContent: JUSTIFY[element.style.verticalAlign],
        alignItems: ALIGN[element.style.align],
        opacity: placeholder ? 0.55 : boxStyle(element).opacity,
      }}
    >
      {frame ? <CaptionFrameView element={element} frame={frame} /> : null}
    </div>
  );
}
